// encryptionService.ts
// AES-256-GCM JSON envelope for React (browser, Web Crypto API)

function getCrypto(): Crypto {
  const c = (globalThis as any).crypto;
  if (!c || !c.subtle || typeof c.getRandomValues !== 'function') {
    throw new Error('Web Crypto API not available in this environment');
  }
  return c as Crypto;
}

function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
  // Always return a concrete ArrayBuffer (avoid SAB unions in TS libs)
  const ab = u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
  return ab as ArrayBuffer;
}

export type EncryptedPayload = {
  nonce: string;      // base64
  ciphertext: string; // base64 (without tag)
  tag: string;        // base64 (16 bytes)
  aad?: string;       // base64 (optional AAD)
};

export interface EncryptionService {
  encryptJson(json: Record<string, unknown>, opts?: { aad?: string }): Promise<EncryptedPayload>;
  decryptToJson(payload: EncryptedPayload): Promise<Record<string, unknown>>;
}

const TAG_LENGTH_BYTES = 16; // 128-bit tag
const NONCE_LENGTH_BYTES = 12; // recommended for GCM

export class AesGcmEncryptionService implements EncryptionService {
  private key: CryptoKey;

  /**
   * Construct with a CryptoKey (AES-GCM 256) or a base64 32-byte key string.
   */
  static async fromBase64Key(base64Key: string): Promise<AesGcmEncryptionService> {
    const raw = base64ToBytes(base64Key);
    if (raw.length !== 32) {
      throw new Error(`Key must be 32 bytes (got ${raw.length})`);
    }
    const key = await getCrypto().subtle.importKey(
      "raw",
      toArrayBuffer(raw),
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
    return new AesGcmEncryptionService(key);
  }

  constructor(key: CryptoKey) {
    this.key = key;
  }

  async encryptJson(json: Record<string, unknown>, opts?: { aad?: string }): Promise<EncryptedPayload> {
    const encoder = new TextEncoder();
    const plaintext = encoder.encode(JSON.stringify(json));
    const nonce = randomBytes(NONCE_LENGTH_BYTES);

    // AAD: text -> bytes (optional)
    const aadBytes = opts?.aad ? encoder.encode(opts.aad) : undefined;

    // WebCrypto returns ciphertext||tag in one buffer
    const ctWithTag = new Uint8Array(
      await getCrypto().subtle.encrypt(
        {
          name: "AES-GCM",
          iv: toArrayBuffer(nonce),
          additionalData: aadBytes ? toArrayBuffer(aadBytes) : undefined,
          tagLength: TAG_LENGTH_BYTES * 8,
        },
        this.key,
        toArrayBuffer(plaintext)
      )
    );

    const { ct, tag } = splitCiphertextAndTag(ctWithTag, TAG_LENGTH_BYTES);

    return {
      nonce: bytesToBase64(nonce),
      ciphertext: bytesToBase64(ct),
      tag: bytesToBase64(tag),
      ...(opts?.aad ? { aad: bytesToBase64(encoder.encode(opts.aad)) } : {}),
    };
  }

  async decryptToJson(payload: EncryptedPayload): Promise<Record<string, unknown>> {
    const decoder = new TextDecoder();
    const nonce = base64ToBytes(payload.nonce);
    const ct = base64ToBytes(payload.ciphertext);
    const tag = base64ToBytes(payload.tag);

    // Re-join ct||tag because WebCrypto expects them together
    const ctWithTag = joinCiphertextAndTag(ct, tag);

    const aadBytes = payload.aad ? base64ToBytes(payload.aad) : undefined;

    const clear = new Uint8Array(
      await getCrypto().subtle.decrypt(
        {
          name: "AES-GCM",
          iv: toArrayBuffer(nonce),
          additionalData: aadBytes ? toArrayBuffer(aadBytes) : undefined,
          tagLength: TAG_LENGTH_BYTES * 8,
        },
        this.key,
        toArrayBuffer(ctWithTag)
      )
    );

    return JSON.parse(decoder.decode(clear)) as Record<string, unknown>;
  }
}

/* -------------------------- helpers -------------------------- */

function randomBytes(n: number): Uint8Array {
  const arr = new Uint8Array(n);
  getCrypto().getRandomValues(arr);
  return arr;
}

function splitCiphertextAndTag(ctWithTag: Uint8Array, tagLenBytes: number) {
  if (ctWithTag.length < tagLenBytes) {
    throw new Error("ciphertext too short for tag");
  }
  const ct = ctWithTag.subarray(0, ctWithTag.length - tagLenBytes);
  const tag = ctWithTag.subarray(ctWithTag.length - tagLenBytes);
  return { ct, tag };
}

function joinCiphertextAndTag(ct: Uint8Array, tag: Uint8Array) {
  const out = new Uint8Array(ct.length + tag.length);
  out.set(ct, 0);
  out.set(tag, ct.length);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  // Browser path
  // Use btoa with chunked conversion to binary string
  const g: any = globalThis as any;
  if (typeof g.btoa === 'function') {
    let binary = '';
    const chunk = 0x8000; // 32KB
    for (let i = 0; i < bytes.length; i += chunk) {
      const sub = bytes.subarray(i, i + chunk);
      // String.fromCharCode(...sub) can overflow the argument limit without chunking
      binary += String.fromCharCode.apply(null, Array.from(sub));
    }
    return g.btoa(binary);
  }
  // Node path
  if (typeof (g.Buffer) !== 'undefined') {
    return (g.Buffer as any).from(bytes).toString('base64');
  }
  throw new Error('Base64 encoding not available in this environment');
}

function base64ToBytes(b64: string): Uint8Array {
  const g: any = globalThis as any;
  if (typeof g.atob === 'function') {
    const binary = g.atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }
  if (typeof (g.Buffer) !== 'undefined') {
    return new Uint8Array((g.Buffer as any).from(b64, 'base64'));
  }
  throw new Error('Base64 decoding not available in this environment');
}